import { createAsyncThunk } from '@reduxjs/toolkit';
import { AssetType, IAsset } from 'types/app/asset';
import { AxiosWrapper } from './axios.wrapper';
import { updateAsset } from 'core/slices/assets';
import { LoadingState } from 'types/domain/base';
import { loaders } from 'core/sagas/product/assets/load';
//import { AudioLoader } from 'three/src/loaders/AudioLoader';

import axios, { CancelTokenSource } from 'axios';
import { IUploadFile, uploadActions } from 'core/slices/upload';
import { IKeyArray } from 'types/IKeyArray';
import { debounce } from 'lodash';
import { isIOS, isSafari } from 'react-device-detect';
import { dashjs } from 'utils/loadDash';
import { errorRequest } from './product';
import { IError } from 'types/app/error';

export const getAsset = createAsyncThunk('asset/get', async (data: Partial<IAsset>) => {
  const response = await AxiosWrapper.getAsset(data.url);
  return response.data;
});

export const loadAsset = createAsyncThunk(
  'asset/load',
  async (data: IAsset, { dispatch, rejectWithValue, fulfillWithValue }) => {
    try {
      if (data.type === AssetType.VIDEO) {
        if (isIOS || isSafari) {
          await fetch(data.url, { credentials: 'include' });
          fulfillWithValue(data);
        } else {
          const dash = await dashjs();
          loaders[data.id] = dash.MediaPlayer().create();
          loaders[data.id].initialize(null, data.url || data.fallback, false, data.start);
          loaders[data.id].updateSettings({
            //debug: { logLevel: 5 },
            streaming: {
              cacheInitSegments: true,
              abr: {
                //initialBitrate: { audio: 1, video: 1000 },
                autoSwitchBitrate: { audio: true, video: true },
              },
            },
          });
          loaders[data.id].on(dash.MediaPlayer.events.ERROR, e => rejectWithValue(e));
          loaders[data.id].on('manifestLoaded', e => fulfillWithValue(data));
          loaders[data.id].preload();
        }
      } else {
        if (data.type === AssetType.IMAGE) {
          const file = await fetch(data.url, { credentials: 'include' });
          //@ts-ignore
          const { TextureLoader } = await import('three/src/loaders/TextureLoader');
          loaders[data.id] = new TextureLoader();
        }
        //if (data.type === AssetType.AUDIO) loaders[data.id] = new AudioLoader();
        if (data.type === AssetType.MODEL) {
          //@ts-ignore
          const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader');
          //@ts-ignore
          const { DRACOLoader } = await import('three/examples/jsm/loaders/DRACOLoader');
          loaders[data.id] = new GLTFLoader();
          loaders[data.id].draco = new DRACOLoader();
          loaders[data.id].draco.setDecoderPath('/libs/draco/');
          loaders[data.id].draco.preload();
          loaders[data.id].setDRACOLoader(loaders[data.id].draco);
        }

        loaders[data.id].asset = await loaders[data.id].loadAsync(
          data.url,
          debounce(
            event => {
              dispatch(
                updateAsset({
                  id: data.id,
                  loading: event.loaded / event.total < 1 ? LoadingState.LOADING : LoadingState.LOADED,
                  progress: event.loaded / event.total,
                }),
              );
            },

            100,
          ),
        );
      }

      dispatch(updateAsset({ id: data.id, loading: LoadingState.LOADED, progress: 1 }));
      fulfillWithValue(true);
    } catch (e: any) {
      rejectWithValue(e);
      dispatch(updateAsset({ id: data.id, loading: LoadingState.ERROR }));
      dispatch(errorRequest({ code: 3, message: e.message, stackTrace: 'asset load failed' } as IError));
    }
  },
);

export const CancelTokens: IKeyArray<CancelTokenSource> = {};

export const uploadRequest = createAsyncThunk('api/admin/upload/file', async (data: IUploadFile, { dispatch }) => {
  const file = await fetch(data.ref).then(r => r.blob());
  CancelTokens[data.id] = axios.CancelToken.source();
  if (!data.sceneConfigId) URL.revokeObjectURL(data.ref);

  //dispatch(uploadActions.uploadStarted({ data, cancelToken: cancelToken.token }));

  const response = await axios.put(data.signedUrl, file, {
    cancelToken: CancelTokens[data.id].token,
    headers: { 'Content-Type': data.contentType },
    onUploadProgress: progressEvent =>
      dispatch(
        uploadActions.uploadProgress({
          id: data.id,
          progress: Math.round((progressEvent.loaded * 100) / progressEvent.total) / 100,
        }),
      ),
  });
  return response.data;
});
